import { DEFAULT_DARK_THEME, DEFAULT_LIGHT_THEME } from '~constants/theming';
import { Branding } from '~schemas/branding';
import { Theming } from '~schemas/settings';

/**
 * Merge the /v2/branding API response onto the hardcoded defaults.
 *
 * Mapping:
 *   colours.brand.primary  → surface.focus
 *   colours.brand.surface  → surface.primary
 *   colours.text.heading   → text.heading
 *   colours.text.body      → text.body
 *   fonts                  → fonts (fully replaced by branding value)
 */
export function buildThemingFromBranding(branding: Branding): Theming {
  const defaultTheme = branding.dark_mode ? DEFAULT_DARK_THEME : DEFAULT_LIGHT_THEME;

  return {
    ...defaultTheme,
    fonts: branding.fonts,
    surface: {
      ...defaultTheme.surface,
      focus: branding.colours.brand.primary,
      primary: branding.colours.brand.surface,
    },
    text: {
      ...defaultTheme.text,
      heading: branding.colours.text.heading,
      body: branding.colours.text.body,
    },
  };
}

export function generateFontUrls(theme: Theming): string[] {
  const urls: string[] = [];

  Object.values(theme.fonts).forEach((v) => {
    if (v.url && v.url.startsWith('https://')) {
      urls.push(v.url);
      return;
    }

    // Google fonts
    const fontWeights = ['400', '500', '600', '700', '800'];
    const fontUrl = `https://fonts.googleapis.com/css?family=${v.family}:${fontWeights.join(',')}`;
    urls.push(fontUrl);
  });

  return urls;
}

export function applyFont(url: string) {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url;
  document.head.appendChild(link);
}

function applyMapping(mapping: Record<string, string>, values: Record<string, unknown>) {
  Object.entries(mapping).forEach(([key, cssVar]) => {
    const mappedValue = values[key];
    if (mappedValue) {
      const value =
        typeof mappedValue === 'object' && 'family' in mappedValue && typeof mappedValue.family === 'string'
          ? mappedValue.family
          : mappedValue;

      if (typeof value !== 'string') return;
      document.documentElement.style.setProperty(cssVar, value);
    }
  });
}

export function applyTheming(theming: Theming) {
  const mappings: Record<keyof Theming, Record<string, string>> = {
    fonts: {
      heading: '--font-heading',
      body: '--font-body',
    },
    surface: {
      primary: '--color-surface-primary',
      primary_support: '--color-surface-primary-support',
      primary_support_soft: '--color-surface-primary-support-soft',
      neutral: '--color-surface-neutral',
      focus: '--color-surface-focus',
      positive: '--color-surface-positive',
      positive_support: '--color-surface-positive-support',
      warning: '--color-surface-warning',
      warning_support: '--color-surface-warning-support',
      error: '--color-surface-error',
      error_support: '--color-surface-error-support',
      shadow: '--color-surface-shadow',
      hover: '--color-surface-hover',
      video_overlay_button: '--color-surface-video-overlay-button',
      modal_overlay: '--color-surface-modal-overlay',
    },
    border: {
      primary: '--color-border-primary',
      disabled: '--color-border-disabled',
      hover: '--color-border-hover',
    },
    text: {
      heading: '--color-text-heading',
      body: '--color-text-body',
      secondary: '--color-text-secondary',
      focus: '--color-text-focus',
      disabled: '--color-text-disabled',
      overlay_disabled: '--color-text-overlay-disabled',
    },
  };

  // Fix: Explicitly assert that section is a keyof Theming
  (Object.keys(mappings) as (keyof Theming)[]).forEach((section) => {
    applyMapping(mappings[section], theming[section] || {});
  });

  // Apply fonts
  generateFontUrls(theming).forEach((url) => {
    applyFont(url);
  });
}
